import React, { useState } from "react";
import "../styles/Skills.css";
import {
  FaCode,
  FaServer,
  FaDatabase,
  FaTools,
  FaClipboardList,
  FaLightbulb,
  FaUsers,
  FaClock,
  FaCodeBranch,
  FaBrain,
  FaSync,
} from "react-icons/fa";

/** Prefer PNG in /public/assets/; falls back to SVG if the PNG is missing. */
function SkillTechIcon({ skill }) {
  const [src, setSrc] = useState(skill.image);
  if (!skill.image) {
    return (
      <span className="skill-text-badge" title={skill.name}>
        {skill.name}
      </span>
    );
  }
  return (
    <img
      src={`${process.env.PUBLIC_URL}${src}`}
      alt={skill.name}
      className="skill-image"
      onError={() => {
        if (skill.imageFallback && src === skill.image) {
          setSrc(skill.imageFallback);
        }
      }}
    />
  );
}

const Skills = () => {
  /** Mirrors resume: languages, frameworks, data, tools, practices */
  const technicalSkills = [
    {
      category: "Languages",
      icon: <FaCode />,
      skills: [
        { name: "HTML", image: "/assets/HTML.svg" },
        { name: "CSS", image: "/assets/CSS.svg" },
        { name: "JavaScript", image: "/assets/JS.svg" },
        { name: "TypeScript", image: "/assets/TS.svg" },
        { name: "Python", image: "/assets/Python.png" },
        { name: "Java", image: "/assets/java.svg" },
        { name: "SQL", image: "/assets/sql.svg" },
      ],
    },
    {
      category: "Frameworks & runtime",
      icon: <FaServer />,
      skills: [
        { name: "React", image: "/assets/REACT.svg" },
        { name: "Node.js", image: "/assets/NODEJS.svg" },
        { name: "Express.js", image: "/assets/express.webp" },
        {
          name: "Spring",
          image: "/assets/spring.jpeg",
          imageFallback: "/assets/spring.svg",
        },
      ],
    },
    {
      category: "Data & persistence",
      icon: <FaDatabase />,
      skills: [
        {
          name: "PostgreSQL",
          image: "/assets/postgre.jpeg",
          imageFallback: "/assets/postgresql.svg",
        },
        { name: "MySQL", image: "/assets/MySQL.png" },
        { name: "MongoDB", image: "/assets/MongoDB.png" },
      ],
    },
    {
      category: "Tools & platforms",
      icon: <FaTools />,
      skills: [
        { name: "Git", image: "/assets/git.svg" },
        { name: "GitHub", image: "/assets/GitHubCard.png" },
        { name: "GitLab", image: "/assets/gitlab.svg" },
        { name: "VS Code", image: "/assets/vscode.png" },
        { name: "Figma", image: "/assets/Figma.png" },
        {
          name: "Docker",
          image: "/assets/docker.jpeg",
          imageFallback: "/assets/docker.svg",
        },
        { name: "Netlify", image: "/assets/netlify.svg" },
        {
          name: "AWS Lambda",
          image: "/assets/aws-lambda.png",
          imageFallback: "/assets/aws-lambda.svg",
        },
        { name: "S3", image: "/assets/aws-s3.svg" },
        { name: "Amazon RDS", image: "/assets/aws-rds.svg" },
        { name: "Amazon Quicksight", image: "/assets/aws-quicksight.svg" },
      ],
    },
    {
      category: "Practices",
      icon: <FaClipboardList />,
      skills: [
        { name: "REST APIs", image: "/assets/rest-apis.svg" },
        { name: "JWT auth", image: "/assets/jwt-auth.svg" },
        { name: "Schema migrations", image: "/assets/schema-migrations.svg" },
      ],
    },
  ];

  const professionalSkills = [
    {
      name: "Problem-Solving",
      icon: <FaLightbulb />,
      description: "Breaking down complex challenges into elegant solutions"
    },
    {
      name: "Communication",
      icon: <FaUsers />,
      description: "Translating technical concepts for diverse audiences"
    },
    {
      name: "Time Management",
      icon: <FaClock />,
      description: "Prioritizing effectively to meet project deadlines"
    },
    {
      name: "Critical Thinking",
      icon: <FaBrain />,
      description: "Evaluating tradeoffs and evidence before committing to a path"
    },
    {
      name: "Collaboration",
      icon: <FaCodeBranch />,
      description: "Working within teams to achieve optimal results"
    },
    {
      name: "Agile",
      icon: <FaSync />,
      description: "Iterating with feedback, sprints, and clear delivery goals"
    }
  ];

  const certifications = [
    { name: "AWS Cloud Practitioner", image: "/assets/certR1.png" },
    { name: "CompTIA Project+", image: "/assets/certR2.png" },
    { name: "ITIL v4 Foundation", image: "/assets/certL1.png" },
    { name: "Google IT Support Professional", image: "/assets/certL2.png" }
  ];

  return (
    <div className="skills-container">
      <div className="skills-header">
        <span className="mono-label">02/</span>
        <h1>Skills</h1>
      </div>
      
      <div className="skills-intro">
        <p>
          Technical profile aligned with my resume: languages and frameworks, databases,
          cloud and tooling, plus engineering practices—paired with the professional skills below.
        </p>
      </div>
      
      <div className="a24-skill-grid">
        <div className="tech-skills-section">
          <div className="section-header">
            <div className="section-indicator">
              <span className="indicator-number">01</span>
              <div className="indicator-line"></div>
            </div>
            <h2>Technical Expertise</h2>
          </div>
          
          <div className="tech-categories">
            {technicalSkills.map((category, idx) => (
              <div className="tech-category" key={idx}>
                <div className="category-header">
                  <span className="category-icon">{category.icon}</span>
                  <h3>{category.category}</h3>
                </div>
                
                <div className="skills-gallery">
                  {category.skills.map((skill, skillIdx) => (
                    <div className="skill-card" key={`${category.category}-${skill.name}-${skillIdx}`}>
                      <div className="skill-image-container">
                        <SkillTechIcon skill={skill} />
                      </div>
                      <span className="skill-name">{skill.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="divider"></div>
        
        <div className="certifications-section">
          <div className="section-header">
            <div className="section-indicator">
              <span className="indicator-number">02</span>
              <div className="indicator-line"></div>
            </div>
            <h2>Certifications</h2>
          </div>
          
          <div className="certifications-row">
            {certifications.map((cert, idx) => (
              <div className="certification-card" key={idx}>
                <div className="certification-image-container">
                  <img 
                    src={`${process.env.PUBLIC_URL}${cert.image}`} 
                    alt={cert.name}
                    className="certification-image" 
                  />
                </div>
                <div className="certification-name">
                  <span>{cert.name}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="divider"></div>
        
        <div className="professional-skills-section">
          <div className="section-header">
            <div className="section-indicator">
              <span className="indicator-number">03</span>
              <div className="indicator-line"></div>
            </div>
            <h2>Professional Skills</h2>
          </div>
          
          <p className="section-quote">
            "Technical skills make solutions possible.<br />
            Professional skills make them valuable."
          </p>
          
          <div className="professional-skills-list">
            {professionalSkills.map((skill, idx) => (
              <div className="professional-skill" key={idx}>
                <div className="skill-label">
                  <span className="skill-icon">{skill.icon}</span>
                  <h3>{skill.name}</h3>
                </div>
                <p className="skill-description">{skill.description}</p>
              </div>
            ))}
          </div>
          
          <div className="skills-note">
            <span className="note-marker">Note</span>
            <p>Continuously exploring new technologies and methodologies to enhance development capabilities and deliver exceptional results.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Skills;
